import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  CreditCard,
  TrendingUp,
  Users,
  Settings,
  Wrench,
  ListTodo,
  Receipt,
  ChevronDown,
  LogOut,
  Menu,
  X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

interface NavChild {
  path: string;
  label: string;
  icon: typeof LayoutDashboard;
}

interface NavItem {
  path: string;
  label: string;
  icon: typeof LayoutDashboard;
  children?: NavChild[];
}

const navItems: NavItem[] = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/subscriptions', label: 'Subscriptions', icon: CreditCard },
  { path: '/projection', label: 'Projection', icon: TrendingUp },
  {
    path: '/tools',
    label: 'Tools',
    icon: Wrench,
    children: [
      { path: '/tools/todo', label: 'Todo', icon: ListTodo },
      { path: '/tools/expenses', label: 'Expense Tracker', icon: Receipt },
    ],
  },
  { path: '/admin', label: 'Admin', icon: Users },
  { path: '/settings', label: 'Settings', icon: Settings },
];

export default function Sidebar({ isOpen, onToggle }: SidebarProps) {
  const { logout, user } = useAuth();
  const location = useLocation();
  const isRootAdmin = Boolean(user?.isRootAdmin);
  const isFamilyMember = Boolean(user?.isFamilyMember);

  const hiddenForRoot = ['/', '/subscriptions', '/projection', '/tools'];
  const hiddenForFamilyMember = ['/admin', '/settings'];
  const visibleNavItems = navItems.filter((item) => {
    if (isRootAdmin && hiddenForRoot.includes(item.path)) return false;
    if (isFamilyMember && hiddenForFamilyMember.includes(item.path)) return false;
    return true;
  });

  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>(() => {
    // Auto-expand if currently on a child route
    const initial: Record<string, boolean> = {};
    for (const item of navItems) {
      if (item.children?.some((c) => location.pathname.startsWith(c.path))) {
        initial[item.path] = true;
      }
    }
    return initial;
  });

  const toggleMenu = (path: string) => {
    setExpandedMenus((prev) => ({ ...prev, [path]: !prev[path] }));
  };

  const handleLogout = async () => {
    await logout();
  };

  return (
    <>
      {/* Mobile Header Bar */}
      <div className="fixed top-0 left-0 right-0 h-14 bg-dark-800 border-b border-dark-700 flex items-center px-4 z-30 lg:hidden">
        <button
          onClick={onToggle}
          className="p-2 text-dark-300 hover:text-white hover:bg-dark-700 rounded-lg transition-colors"
          aria-label="Toggle menu"
        >
          {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
        <h1 className="ml-3 text-lg font-bold text-white flex items-center gap-2">
          <span className="w-7 h-7 bg-primary-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">
            S
          </span>
          SubManager
        </h1>
      </div>

      {/* Spacer for mobile header */}
      <div className="h-14 lg:hidden flex-shrink-0" />

      {/* Sidebar */}
      <aside 
        className={`
          fixed lg:relative inset-y-0 left-0 z-50
          w-64 bg-dark-800 border-r border-dark-700 flex flex-col
          transform transition-transform duration-300 ease-in-out
          lg:transform-none lg:translate-x-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          top-0 lg:top-0
        `}
      >
        {/* Desktop Logo */}
        <div className="p-6 border-b border-dark-700 hidden lg:block">
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <span className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center text-white font-bold">
              S
            </span>
            SubManager
          </h1>
        </div>

        {/* Mobile close button in sidebar */}
        <div className="p-4 border-b border-dark-700 flex items-center justify-between lg:hidden">
          <h1 className="text-lg font-bold text-white flex items-center gap-2">
            <span className="w-7 h-7 bg-primary-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">
              S
            </span>
            SubManager
          </h1>
          <button
            onClick={onToggle}
            className="p-2 text-dark-300 hover:text-white hover:bg-dark-700 rounded-lg transition-colors"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-1">
            {visibleNavItems.map((item) => {
              // Expandable parent with children
              if (item.children && item.children.length > 0) {
                const isExpanded = expandedMenus[item.path] ?? false;
                const isChildActive = item.children.some((c) => location.pathname.startsWith(c.path));
                return (
                  <li key={item.path}>
                    <button
                      type="button"
                      onClick={() => toggleMenu(item.path)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        isChildActive
                          ? 'bg-primary-500/10 text-primary-400'
                          : 'text-dark-300 hover:bg-dark-700 hover:text-white'
                      }`}
                    >
                      <item.icon className="w-5 h-5 flex-shrink-0" />
                      <span className="flex-1 text-left">{item.label}</span>
                      <ChevronDown
                        className={`w-4 h-4 transition-transform duration-200 ${
                          isExpanded ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {isExpanded && (
                      <ul className="mt-1 ml-4 space-y-1">
                        {item.children.map((child) => (
                          <li key={child.path}>
                            <NavLink
                              to={child.path}
                              className={({ isActive }) =>
                                `flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${
                                  isActive
                                    ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                                    : 'text-dark-300 hover:bg-dark-700 hover:text-white'
                                }`
                              }
                            >
                              <child.icon className="w-4 h-4 flex-shrink-0" />
                              <span>{child.label}</span>
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              }

              // Regular nav item
              return (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                          : 'text-dark-300 hover:bg-dark-700 hover:text-white'
                      }`
                    }
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    <span>{item.label}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>
        
        <div className="p-4 border-t border-dark-700">
          <button
            type="button"
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-dark-300 hover:bg-dark-700 hover:text-white transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span>Logout</span>
          </button>
          <div className="text-xs text-dark-500 mt-3">
            Subscription Manager v1.0
          </div>
        </div>
      </aside>
    </>
  );
}
